import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './css/chatbot.css';

const ChatbotPage = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hello! I'm your Edinburgh travel assistant. I can help you discover attractions, plan your itinerary, and answer questions about the city. How can I assist you today?",
      sender: 'bot',
      profile: '/favicon.svg'
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [showPrototypeModal, setShowPrototypeModal] = useState(true);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-resize textarea as user types
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'inherit';
      const scrollHeight = textarea.scrollHeight;
      textarea.style.height = `${scrollHeight}px`;
    }
  }, [inputMessage]);

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      files.forEach(file => {
        // In a real implementation, you would handle file upload here
        // For now, we'll just show the file as a message
        const reader = new FileReader();
        reader.onload = () => {
          if (file.type.startsWith('image/')) {
            setMessages(prev => [...prev, {
              id: Date.now(),
              text: '',
              image: reader.result,
              fileName: file.name,
              sender: 'user',
              profile: '/favicon.svg'
            }]);
          } else {
            setMessages(prev => [...prev, {
              id: Date.now(),
              text: `Attached file: ${file.name}`,
              sender: 'user',
              profile: '/favicon.svg'
            }]);
          }
        };
        if (file.type.startsWith('image/')) {
          reader.readAsDataURL(file);
        } else {
          reader.readAsText(file);
        }
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputMessage.trim()) {
      setMessages(prev => [...prev, {
        id: Date.now(),
        text: inputMessage,
        sender: 'user',
        profile: '/favicon.svg'
      }]);
      setInputMessage('');
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = '44px';
      }
    }
  };

  return (
    <motion.div 
      className="chatbot-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="chat-container">
        {/* Header with backdrop image */}
        <div className="chat-header">
          <div className="chat-header-content">
            <div className="bot-avatar">
              <img src="/favicon.svg" alt="AI Assistant" />
              <span className="bot-status"></span>
            </div>
            <div className="bot-info">
              <h2>AI Travel Assistant</h2>
              <p>Online - Ready to help</p>
            </div>
          </div>
        </div>

        {/* Messages area */}
        <div className="messages-container">
          {messages.map((msg) => (
            <motion.div 
              key={msg.id} 
              className={`message ${msg.sender}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="sender-profile">
                <img src={msg.profile} alt={msg.sender} />
              </div>
              <div className="message-content">
                {msg.image ? (
                  <div className="message-image">
                    <img src={msg.image} alt={msg.fileName} />
                    <span className="image-filename">{msg.fileName}</span>
                  </div>
                ) : (
                  msg.text
                )}
              </div>
            </motion.div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Reply section */}
        <form onSubmit={handleSubmit} className="reply-section">
          <div className="reply-actions">
            <button 
              type="button" 
              className="attach-button"
              onClick={handleFileButtonClick}
              disabled={showPrototypeModal}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
              </svg>
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
              multiple
              accept="image/*,.pdf,.doc,.docx,.txt"
            />
          </div>
          <textarea 
            ref={textareaRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Type your message here..."
            disabled={showPrototypeModal}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
          />
          <button 
            type="submit" 
            className="submit-button"
            disabled={showPrototypeModal || !inputMessage.trim()}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          </button>
        </form>

        {/* Quick Actions */}
        <div className="quick-actions">
          <button className="quick-action-btn" disabled={showPrototypeModal}>
            Popular Attractions
          </button>
          <button className="quick-action-btn" disabled={showPrototypeModal}>
            Plan My Visit
          </button>
          <button className="quick-action-btn" disabled={showPrototypeModal}>
            Local Tips
          </button>
          <button className="quick-action-btn" disabled={showPrototypeModal}>
            Transport Info
          </button>
        </div>
      </div>

      {/* Prototype Modal */}
      <AnimatePresence>
        {showPrototypeModal && (
          <motion.div 
            className="prototype-modal"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
          >
            <div className="modal-content">
              <div className="modal-icon">
                <img src="/favicon.svg" alt="AI Assistant" />
              </div>
              <h3>Prototype Feature</h3>
              <p>This AI chatbot feature is currently a prototype and is not yet functional. The full version will be available soon!</p>
              <button onClick={() => setShowPrototypeModal(false)}>Start Exploring</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ChatbotPage;